import { formatLocalDate } from './lib/writingDays'

type AgentEventCallback = (event: AgentStreamEvent) => void
const activeAgentRequests = new Map<string, AbortController>()

/** Throws the server's own error message rather than a bare status, which users end up seeing. */
async function readJson(res: Response): Promise<any> {
  if (!res.ok) {
    const failure = await res.json().catch(() => null)
    throw new Error(failure?.error || `HTTP error! status: ${res.status}`)
  }
  return res.json()
}

const api = {
  settings: {
    get: async () => {
      const res = await fetch('/api/settings')
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    save: async (settings: any) => {
      const res = await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    }
  },
  profiles: {
    getActive: async () => {
      const res = await fetch('/api/profiles/active')
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    update: async (
      id: string,
      profile: { penName: string; authorBlurb: string; libraryPublic?: boolean }
    ) => {
      const res = await fetch(`/api/profiles/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    uploadAvatar: async (id: string, file: File) => {
      const body = new FormData()
      body.append('avatar', file)
      // No Content-Type header: the browser has to set it so the multipart boundary is right.
      const res = await fetch(`/api/profiles/${encodeURIComponent(id)}/avatar`, {
        method: 'POST',
        body
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    removeAvatar: async (id: string) => {
      const res = await fetch(`/api/profiles/${encodeURIComponent(id)}/avatar`, {
        method: 'DELETE'
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    listDefaultAvatars: async () => {
      const res = await fetch('/api/default-avatars')
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      const body = await res.json()
      return body.avatars
    },
    selectDefaultAvatar: async (id: string, defaultAvatarId: string) => {
      const res = await fetch(`/api/profiles/${encodeURIComponent(id)}/avatar/default`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaultAvatarId })
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    }
  },
  stories: {
    list: async () => {
      const res = await fetch('/api/stories')
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    create: async (name: string) => {
      const res = await fetch('/api/stories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    load: async (id: string) => {
      const res = await fetch(`/api/stories/${id}`)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    save: async (id: string, storyData: any, baseWikiRevision?: number) => {
      const res = await fetch(`/api/stories/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The writer's own local date, so a session running past midnight is filed on the
        // day they'd say they wrote it. Stamped here rather than at each call site so no
        // save path can forget it. baseWikiRevision is the wiki_revision the client's wiki
        // array was built from; the server skips the destructive delete when it's stale.
        body: JSON.stringify({
          ...storyData,
          localDate: formatLocalDate(new Date()),
          baseWikiRevision
        })
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    delete: async (id: string) => {
      const res = await fetch(`/api/stories/${id}`, {
        method: 'DELETE'
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    wikiHome: async (id: string) => {
      const res = await fetch(`/api/stories/${id}/wiki/home`)
      if (!res.ok) throw new Error('Failed to load Wiki Home')
      return res.json()
    },
    parseOnboardingItem: async (id: string, item: OnboardingParseItem) => {
      const res = await fetch(`/api/stories/${id}/onboarding/parse-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    expandOnboardingPage: async (
      id: string,
      page: ParsedWikiPage,
      context: OnboardingParseItem['context']
    ) => {
      const res = await fetch(`/api/stories/${id}/onboarding/expand-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, context })
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    suggestOnboardingTitle: async (
      id: string,
      content: string,
      context: OnboardingParseItem['context']
    ) => {
      const res = await fetch(`/api/stories/${id}/onboarding/suggest-title`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, context })
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    initializeWiki: async (id: string, onboardingData: any) => {
      const res = await fetch(`/api/stories/${id}/initialize-wiki`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(onboardingData)
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    publication: async (id: string) => {
      const res = await fetch(`/api/stories/${id}/publication`)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    dailyProgress: async (id: string, date: string) => {
      const res = await fetch(
        `/api/stories/${encodeURIComponent(id)}/daily-progress?date=${encodeURIComponent(date)}`
      )
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    generateBlurb: async (id: string, draft: { title: string; genre: string; content: string }) => {
      const res = await fetch(`/api/stories/${id}/publishing/blurb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft)
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    publish: async (id: string, input: PublishStoryInput) => {
      const res = await fetch(`/api/stories/${id}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    cover: async (
      id: string
    ): Promise<{
      coverUrl: string | null
      coverStyle: string | null
      coverPrompt: string | null
    }> => {
      const res = await fetch(`/api/stories/${encodeURIComponent(id)}/cover`)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    generateCover: async (
      id: string,
      payload: { blurb: string; style?: string; note?: string }
    ): Promise<{ coverUrl: string }> => {
      const res = await fetch(`/api/stories/${encodeURIComponent(id)}/cover/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    suggestCoverPrompt: async (
      id: string,
      payload: { style: string; blurb?: string }
    ): Promise<{ prompt: string }> => {
      const res = await fetch(`/api/stories/${encodeURIComponent(id)}/cover/prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    removeCover: async (id: string): Promise<void> => {
      const res = await fetch(`/api/stories/${encodeURIComponent(id)}/cover`, { method: 'DELETE' })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
    },
    openFolder: async () => {
      const res = await fetch('/api/stories/open-folder', {
        method: 'POST'
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    }
  },
  coverStyles: {
    list: async (): Promise<{ styles: CoverStyle[] }> => readJson(await fetch('/api/cover-styles'))
  },
  wiki: {
    proposals: async (storyId: string) => {
      const res = await fetch(`/api/stories/${storyId}/wiki/proposals`)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    resolveProposal: async (storyId: string, proposalId: string, action: 'accept' | 'reject') => {
      const res = await fetch(`/api/stories/${storyId}/wiki/proposals/${proposalId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    syncNow: async (storyId: string) => {
      const res = await fetch(`/api/stories/${storyId}/wiki/sync-now`, { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    }
  },
  writingDays: {
    list: async (from: string, to: string) => {
      const res = await fetch(
        `/api/writing-days?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
      )
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    }
  },
  publishedStories: {
    list: async () => {
      const res = await fetch('/api/public/stories')
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    load: async (publicationId: string, slug: string, version?: number) => {
      const query = version ? `?version=${version}` : ''
      const res = await fetch(
        `/api/public/stories/${encodeURIComponent(publicationId)}/${encodeURIComponent(slug)}${query}`
      )
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    }
  },
  library: {
    list: async () => readJson(await fetch('/api/library'))
  },
  storySocial: {
    save: async (publicationId: string) =>
      readJson(
        await fetch(`/api/published-stories/${encodeURIComponent(publicationId)}/save`, {
          method: 'PUT'
        })
      ),
    unsave: async (publicationId: string) =>
      readJson(
        await fetch(`/api/published-stories/${encodeURIComponent(publicationId)}/save`, {
          method: 'DELETE'
        })
      ),
    rate: async (publicationId: string, rating: { stars: number; review: string }) =>
      readJson(
        await fetch(`/api/published-stories/${encodeURIComponent(publicationId)}/rating`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rating)
        })
      ),
    removeRating: async (publicationId: string) =>
      readJson(
        await fetch(`/api/published-stories/${encodeURIComponent(publicationId)}/rating`, {
          method: 'DELETE'
        })
      ),
    reviews: async (publicationId: string, options?: { limit?: number; offset?: number }) => {
      const query = new URLSearchParams()
      if (options?.limit !== undefined) query.set('limit', String(options.limit))
      if (options?.offset !== undefined) query.set('offset', String(options.offset))
      const suffix = query.toString() ? `?${query}` : ''
      return readJson(
        await fetch(`/api/public/stories/${encodeURIComponent(publicationId)}/reviews${suffix}`)
      )
    }
  },
  authors: {
    get: async (profileId: string) =>
      readJson(await fetch(`/api/public/profiles/${encodeURIComponent(profileId)}`)),
    published: async (profileId: string) =>
      readJson(await fetch(`/api/public/profiles/${encodeURIComponent(profileId)}/published`)),
    library: async (profileId: string) =>
      readJson(await fetch(`/api/public/profiles/${encodeURIComponent(profileId)}/library`)),
    reviews: async (profileId: string) =>
      readJson(await fetch(`/api/public/profiles/${encodeURIComponent(profileId)}/reviews`))
  },
  chapterRevisions: {
    list: async (storyId: string, chapterId: string) => {
      const res = await fetch(
        `/api/stories/${encodeURIComponent(storyId)}/chapters/${encodeURIComponent(chapterId)}/revisions`
      )
      if (!res.ok) throw new Error('Failed to load version history')
      return res.json()
    },
    get: async (storyId: string, chapterId: string, revisionId: string) => {
      const res = await fetch(
        `/api/stories/${encodeURIComponent(storyId)}/chapters/${encodeURIComponent(chapterId)}/revisions/${encodeURIComponent(revisionId)}`
      )
      if (!res.ok) throw new Error('Failed to load version')
      return res.json()
    },
    restore: async (storyId: string, chapterId: string, revisionId: string) => {
      const res = await fetch(
        `/api/stories/${encodeURIComponent(storyId)}/chapters/${encodeURIComponent(chapterId)}/revisions/${encodeURIComponent(revisionId)}/restore`,
        { method: 'POST' }
      )
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || 'Failed to restore version')
      }
      return res.json()
    }
  },
  conversations: {
    saveMessages: async (conversationId: string, messages: Message[]) => {
      const res = await fetch(`/api/conversations/${conversationId}/messages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages })
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    setMode: async (conversationId: string, mode: 'plan' | 'ask' | 'writing') => {
      const res = await fetch(`/api/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    rename: async (conversationId: string, name: string) => {
      const res = await fetch(`/api/conversations/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    },
    markRead: async (conversationId: string) => {
      const res = await fetch(`/api/conversations/${conversationId}/read`, {
        method: 'POST'
      })
      if (!res.ok) {
        const error = await res.json().catch(() => null)
        throw new Error(error?.error || `HTTP error! status: ${res.status}`)
      }
      return res.json()
    }
  },
  agent: {
    run: async (
      storyId: string,
      conversationId: string,
      payload: AgentRunPayload,
      onEvent: AgentEventCallback
    ) => {
      const controller = new AbortController()
      activeAgentRequests.set(payload.requestId, controller)
      try {
        const res = await fetch(`/api/stories/${storyId}/conversations/${conversationId}/agent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal
        })
        if (!res.ok) {
          const error = await res.json().catch(() => null)
          throw new Error(error?.error || `HTTP error! status: ${res.status}`)
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('Response body stream is not readable')
        const decoder = new TextDecoder()
        let buffer = ''
        let finalText = ''
        while (true) {
          const { done, value } = await reader.read()
          buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          for (const line of lines) {
            if (!line.trim()) continue
            const event = JSON.parse(line) as AgentStreamEvent
            if (event.type === 'done') finalText = event.text
            if (event.type === 'error') throw new Error(event.error)
            onEvent(event)
          }
          if (done) break
        }
        return finalText
      } catch (error) {
        if (controller.signal.aborted) throw new Error('Request cancelled.')
        throw error
      } finally {
        if (activeAgentRequests.get(payload.requestId) === controller) {
          activeAgentRequests.delete(payload.requestId)
        }
      }
    },
    cancel: async (requestId: string) => {
      activeAgentRequests.get(requestId)?.abort()
      activeAgentRequests.delete(requestId)
      const res = await fetch('/api/agent/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId })
      })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
    },
    runStatus: async (storyId: string, conversationId: string, since: number, rev: number) => {
      const res = await fetch(
        `/api/stories/${storyId}/conversations/${conversationId}/run?since=${since}&rev=${rev}`
      )
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    },
    activeRun: async (storyId: string) => {
      const res = await fetch(`/api/stories/${storyId}/active-run`)
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    }
  },
  models: {
    list: async () => {
      const res = await fetch('/api/models')
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
      return res.json()
    }
  }
}

// Expose the API to the window object
;(window as any).api = api

export default api
